/**
 * Build-time chain selection for the dApp.
 *
 * The exported `environment` object keeps exactly the flat shape it has always
 * had -- ~20 call sites across hooks, screens and components read
 * `environment.factoryAddress`, `.chainId`, `.chainName`, `.tokenDecimals`,
 * `.rpcUrl` and `.projectId` -- but it is now selected out of a per-chain map
 * rather than being one hard-coded literal.
 *
 * `VITE_CHAIN` picks the entry. Vite replaces `import.meta.env.VITE_CHAIN`
 * with a string literal while bundling, so the choice is BAKED INTO THE
 * BUNDLE: there is no runtime switch, and a bundle built for one chain can
 * never be repointed at another by anything the browser does.
 *
 * It defaults to 'baseSepolia' on purpose. Everything that happens by accident
 * -- a stray tag push, someone running `npm run build` locally, a CI run with
 * no repository variable set -- must produce a testnet bundle. Mainnet has to
 * be asked for explicitly.
 */

// Vite's own type package (`vite/client`) is not referenced by
// tsconfig.react.json, and this is the only file in the app that reads
// `import.meta.env`, so the two fields it needs are declared here. These merge
// with vite/client's interfaces if that reference is ever added.
declare global {
  interface ImportMetaEnv {
    readonly VITE_CHAIN?: string;
    readonly VITE_RPC_URL?: string;
  }
  interface ImportMeta {
    readonly env: ImportMetaEnv;
  }
}

export type ChainKey = 'baseSepolia' | 'mainnet';

/**
 * Which ABI shape the contract at `factoryAddress` actually has. It describes
 * the DEPLOYED BYTECODE, not the newest contract source, and it has to be
 * carried per chain entry because nothing else in this file can express it:
 *
 *   - `chainKey` cannot. It is a separate top-level export, not a field on
 *     AppEnvironment, and the Base Sepolia key has already addressed both a v7
 *     and a v9 factory -- one key cannot name three ABI shapes.
 *   - `incrementalFunding` cannot. It is inferred at runtime from the presence
 *     of pendingOwner(), which v9 and v10 both have, so it reads true for both
 *     and is blind to exactly the distinction that matters here.
 *
 * Getting this wrong is not a cosmetic bug. getLockState() returns 11 words on
 * v7, 14 on v9 and 17 on v10, all under the SAME four-byte selector, so a
 * client that decodes v9 data with the v10 ABI throws on every lock and the
 * whole roster goes dark.
 *
 *   v7  -- the original factory. No two-step ownership, no incremental funding,
 *          getLockState returns 11 fields, one lock per beneficiary looked up
 *          through getLockContract(address).
 *   v9  -- incremental funding. pendingOwner(), getLockState returns 14 fields,
 *          still one lock per beneficiary via getLockContract(address).
 *   v10 -- named multi-grant distributions. getLockState returns 17 fields,
 *          getLockContracts(address) returns an ARRAY, and cancellation,
 *          lock names and setTotalAmount exist. getLockContract(address) is
 *          GONE from this ABI.
 */
export type FactoryAbiVersion = 'v7' | 'v9' | 'v10';

/**
 * The shape every consumer sees. Field types are deliberately widened
 * (`number`, `string`) rather than literal unions: `useWeb3` passes
 * `environment.chainId` straight into wagmi's `switchChain`, and a literal
 * type there would tie the call to whichever chain happened to be selected.
 */
export interface AppEnvironment {
  production: boolean;
  factoryAddress: string;
  /**
   * The ABI shape of the factory at `factoryAddress`. Must be changed in the
   * SAME edit as `factoryAddress`: the pair is the deployment, and half of it
   * is a bundle that cannot read its own chain.
   */
  factoryAbiVersion: FactoryAbiVersion;
  /**
   * MonthlyDistributor, the recurring-payout contract. It is a SEPARATE
   * deployment from the factory and shares nothing with it but the token, so it
   * gets its own field rather than being derived from `factoryAddress`.
   *
   * `null` means "not deployed on this chain yet", and that is a supported
   * state, not an error: the payroll screen renders an explanatory panel and the
   * rest of the dApp is unaffected. That is deliberately UNLIKE
   * `factoryAddress`, whose placeholder throws at the bottom of this file --
   * a missing factory means every read in the app hits the zero address, while a
   * missing distributor means one route has nothing to show.
   */
  distributorAddress: string | null;
  tokenDecimals: number;
  chainId: number;
  chainName: string;
  rpcUrl: string;
  projectId: string;
}

// Reown AppKit Project ID - get your own from https://cloud.reown.com
// Shared by both chains: it identifies the app to WalletConnect's relay, which
// is chain-agnostic.
const PROJECT_ID = 'dfb0bbadae976a58500005b16271ebaa';

/**
 * Stands in for the mainnet factory address until the factory actually exists
 * on chain 1. The zero address is used rather than a plausible-looking one so
 * that anything which slips past the guards below fails immediately and
 * obviously instead of quietly reading an unrelated contract.
 */
const FACTORY_ADDRESS_PLACEHOLDER = '0x0000000000000000000000000000000000000000';

const CHAINS: Record<ChainKey, AppEnvironment> = {
  baseSepolia: {
    production: false,
    // Base Sepolia v10, deployed 2026-09-18 (chain-84532-v10).
    // Named multi-grant distributions: three test grants across two wallets.
    // Ownership handover is complete. Both unlock cliffs and TGE remain unset;
    // the owner pins the dates from the admin panel before funding and claims.
    // Supersedes v9 at 0x74ACF094A1E80C0b5bC28cEF44b96F2023aeC8CF.
    factoryAddress: '0x02B840408fBb65D848BDf813a284db5B4ADaD008',
    factoryAbiVersion: 'v10',
    // MonthlyDistributor on Base Sepolia, deployed 2026-09-23 (payroll-84532-v3).
    // Adds withdrawETH. Handover complete (acceptOwnership sent). Seeded from
    // ignition/payroll.baseSepolia.json with three test payments and one
    // claimer key; the first run paid 5,750 TUSDT and the pool holds the 2,250
    // the 26th needs.
    //
    // Supersedes, both retired and recorded under ignition/deployments/:
    //   0x2f06d71160393264Abe2EDCB56280b065aB309F7 (payroll-84532-v2) -- no
    //     withdrawETH. PAUSED and swept to the owner on 2026-09-23, so nothing
    //     can be claimed from it; its ledger still reads as owing, frozen.
    //   0xe48c3C7f48cd41C9556457617FFA00ba4E9f1ADd (payroll-84532-v1) -- an
    //     empty deploy the roster never reached.
    distributorAddress: '0xc5091F4A094DcCa27378FA69e00Dc487961a481E',
    tokenDecimals: 18,
    chainId: 84532, // Base Sepolia testnet
    chainName: 'Base Sepolia',
    rpcUrl: 'https://sepolia.base.org',
    projectId: PROJECT_ID
  },
  mainnet: {
    production: true,
    // Ethereum mainnet v10, deployed 2026-09-18 (chain-1-v2).
    // Named multi-grant distributions; deployed empty, with schedule dates unset.
    // Supersedes the retired v9 factory at
    // 0x8E58A3AB9BaCe1eB3a03177e4AEF04E82137c106.
    factoryAddress: '0xC141560241b9BA2eCf657fF31718E372B8563E10',
    factoryAbiVersion: 'v10',
    // MonthlyDistributor on Ethereum mainnet, deployed 2026-09-24 (payroll-1-v1),
    // verified on Etherscan. Same build as payroll-84532-v3. Handover complete:
    // owner() is 0x72b81264C45d91c5F95E06d8Bf9BFd9Cc1dc3D43, the factory's owner,
    // and pendingOwner() is 0x0. Deployed empty -- no entries, no claimers, no
    // pool -- so the owner creates entries and appoints the payout job's key from
    // the payroll screen.
    distributorAddress: '0xC3AA4B9e74Fd3eA56dc2951f15Bc154ECdaFB37a',
    // Sahara AI (SAHARA), 0xFDFfB411C4A70AA7C95D5C981a6Fb4Da867e1111, has 18
    // decimals. Pinned as the only token the mainnet factory may hold in
    // deployTargets.ts, which is the source of truth for the deploy side.
    tokenDecimals: 18,
    chainId: 1, // Ethereum mainnet
    chainName: 'Ethereum',
    // Public endpoint, the same default viem ships for `mainnet`, so an
    // unconfigured build still resolves. It is rate limited and shared; point
    // VITE_RPC_URL at a dedicated provider before this carries real traffic.
    rpcUrl: import.meta.env.VITE_RPC_URL ?? 'https://eth.merkle.io',
    projectId: PROJECT_ID
  }
};

const RAW_CHAIN = import.meta.env.VITE_CHAIN;

/*
 * ---------------------------------------------------------------------------
 * GUARDS
 * ---------------------------------------------------------------------------
 * These throw at module load. Vite bundles this file, it never executes it, so
 * by themselves they fire only once a browser has already been served a bad
 * bundle. That was measured, not assumed: `VITE_CHAIN=mainnet npm run build`
 * used to exit 0 and write dist/ happily.
 *
 * They are now the build gate as well. The `sahara-chain-guard` plugin in
 * dapp/vite.config.ts transpiles THIS file, substitutes the env Vite resolved
 * for this build in place of `import.meta.env`, and runs it in Node before
 * bundling starts -- so the throws below are what fails `vite build` and
 * `vite dev`, with a non-zero exit and this exact wording. No copy of the chain
 * table exists anywhere else; the gate runs the real one.
 *
 * Two consequences, both load-bearing:
 *
 *   1. This file has to stay import-free. The gate loads it on its own, outside
 *      the bundler, so an `import` of a sibling module cannot be resolved. The
 *      plugin reports that clearly rather than cryptically, but the constraint
 *      belongs here, where someone would add the import.
 *
 *   2. Keep these throws even though the build now catches the same two cases.
 *      They are the last line of defence for a bundle produced some other way
 *      -- a hand-rolled bundler invocation, a stale dist/ someone republishes,
 *      a future build tool -- and they cost nothing when the values are good.
 *
 * (Failing the build from inside src/ itself was tried and does not work, which
 * is worth recording so nobody re-derives it. The only mechanism that fails a
 * build from a source file is a dynamic `import()` of a missing module in a
 * branch Rollup can prove dead. It folds correctly when Vite has substituted a
 * string literal, but with VITE_CHAIN unset Vite substitutes the bare
 * identifier `undefined` and Rollup refuses to evaluate any comparison against
 * it -- `=== 'mainnet'`, `?? 'baseSepolia'`, `typeof`, template literal and
 * `||` default were all measured, all survive. An unset VITE_CHAIN is the
 * normal local case and has to keep building. Hence a plugin, not a trick.)
 */

// An unrecognised value is a typo, and a typo must not quietly fall back to
// the testnet default -- a production deploy that silently serves a Base
// Sepolia bundle looks exactly like a successful one until somebody tries to
// claim.
// `Object.keys(...).includes(...)`, not `RAW_CHAIN in CHAINS`: `in` walks the
// prototype chain, so VITE_CHAIN=constructor (or toString, valueOf, __proto__)
// sailed through this check and selected an inherited Object.prototype member
// as the environment -- a bundle whose `environment.factoryAddress` is
// undefined, which is exactly the silently-wrong-target outcome this guard
// exists to prevent. Now that the build gate runs this check for real, it was a
// hole in the build gate too: `VITE_CHAIN=constructor npm run build` exited 0.
if (RAW_CHAIN !== undefined && !Object.keys(CHAINS).includes(RAW_CHAIN)) {
  throw new Error(
    [
      `VITE_CHAIN="${RAW_CHAIN}" is not a chain this dApp knows about.`,
      '',
      `Valid values: ${Object.keys(CHAINS).join(', ')}.`,
      'Leaving VITE_CHAIN unset selects baseSepolia.',
      '',
      'This fails rather than falling back, because a typo silently serving a',
      'testnet bundle to production is indistinguishable from a working deploy',
      'until someone tries to claim.'
    ].join('\n')
  );
}

export const chainKey: ChainKey = (RAW_CHAIN ?? 'baseSepolia') as ChainKey;

if (CHAINS[chainKey].factoryAddress === FACTORY_ADDRESS_PLACEHOLDER) {
  throw new Error(
    [
      `The ${chainKey} factory address is still the placeholder (${FACTORY_ADDRESS_PLACEHOLDER}).`,
      '',
      'The factory has not been deployed to this chain yet, so every read would',
      'hit the zero address and every write would be sent into the void.',
      '',
      'Deploy the factory, then set factoryAddress for this chain in',
      'dapp/src/config/environment.ts.'
    ].join('\n')
  );
}

export const environment: AppEnvironment = CHAINS[chainKey];
